import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Layer, Map as MapboxMap, MapProvider, Source, useMap } from 'react-map-gl/mapbox'
import type { MapMouseEvent, MapRef } from 'react-map-gl/mapbox'
import type { Map as MapboxGLMap, MapEvent } from 'mapbox-gl'
import { useMantineColorScheme, useMantineTheme } from '@mantine/core'
import type { FeatureCollection, LineString, Point, Polygon as GeoJsonPolygon } from 'geojson'
import 'mapbox-gl/dist/mapbox-gl.css'
import checkeredCircle from '@images/checkeredCircle.png'
import directionArrow from '@images/directionArrow.png'
import { MAPBOX_PUBLIC_TOKEN } from '@/constants'
import { determineMapCenter } from '@/components/Navigation/utils/mapUtils'
import { useMapImage } from '@/components/Navigation/utils/mapGLHelpers'
import AreaPolygons from './AreaPolygons'
import {
  AREA_POLYGON_FILL_LAYER,
  AREA_POLYGON_LINE_LAYER,
  AREA_POLYGON_SOURCE,
  CONTINUATION_SEGMENTS_LAYER,
  CONTINUATION_SEGMENTS_SOURCE,
  DISTINCT_FINISH_NODE_FILTER,
  EDIT_TAIL_LAYER,
  EDIT_TAIL_SOURCE,
  END_NODE_RIPPLE_LAYER_A,
  END_NODE_RIPPLE_LAYER_B,
  FINISH_NODE_IMAGE_ID,
  FINISH_NODE_LAYER,
  GAP_FIX_LAYER,
  GAP_FIX_SOURCE,
  NODE_HIT_PIXEL_PADDING,
  NODE_LABEL_HALO_WIDTH,
  NODE_LABEL_TEXT_ANCHOR,
  NODE_LABEL_TEXT_OFFSET,
  NODE_LABEL_TEXT_SIZE,
  NODE_VISIBLE_MIN_ZOOM,
  NODES_HIT_LAYER,
  NODES_LABEL_LAYER,
  NODES_LAYER,
  NODES_SOURCE,
  PARKING_POLYGONS_FILL_LAYER,
  PARKING_POLYGONS_LINE_LAYER,
  PARKING_POLYGONS_SOURCE,
  PATH_DIRECTION_ARROW_IMAGE_ID,
  PATH_ONE_WAY_ARROWS_FILTER,
  PATH_SEGMENTS_ARROWS_LAYER,
  PATH_SEGMENTS_LAYER,
  PATH_SEGMENTS_SOURCE,
  PLANNER_CONTINUATION_COLOR,
  PLANNER_CONTINUATION_STROKE,
  PLANNER_END_NODE_COLOR,
  PLANNER_END_NODE_RIPPLE_COLOR,
  PLANNER_END_NODE_STROKE,
  PLANNER_FALLBACK_ZOOM,
  PLANNER_FIT_BOUNDS_DURATION_MS,
  PLANNER_FIT_BOUNDS_MAX_ZOOM,
  PLANNER_FIT_BOUNDS_PADDING,
  PLANNER_GAP_FIX_COLOR,
  PLANNER_HINT_COLOR,
  PLANNER_HINT_DASHARRAY,
  PLANNER_HINT_LINE_WIDTH,
  PLANNER_MAP_ID,
  PLANNER_MUTED_COLOR,
  PLANNER_NON_ROUTABLE_NODE_COLOR,
  PLANNER_NON_ROUTABLE_NODE_OPACITY,
  PLANNER_NON_ROUTABLE_NODE_STROKE,
  PLANNER_PARKING_FILL_COLOR,
  PLANNER_PARKING_FILL_OPACITY,
  PLANNER_PARKING_LINE_COLOR,
  PLANNER_PARKING_LINE_WIDTH,
  PLANNER_PATH_COLOR,
  PLANNER_START_NODE_COLOR,
  PLANNER_START_NODE_STROKE,
  PLANNER_STREET_COLOR,
  SHORTEST_PATH_LAYER,
  SHORTEST_PATH_SOURCE,
  SIGN_POST_AZIMUTH_VISIBLE_MIN_ZOOM,
  SIGN_POST_AZIMUTHS_LAYER,
  SIGN_POST_AZIMUTHS_SOURCE,
  SIGN_POST_VISIBLE_MIN_ZOOM,
  SIGN_POSTS_LAYER,
  SIGN_POSTS_SOURCE,
  START_NODE_RIPPLE_LAYER_A,
  START_NODE_RIPPLE_LAYER_B,
  STREET_ARROW_ICON_SIZE,
  STREET_ARROW_OPACITY,
  STREET_ARROW_VISIBLE_MIN_ZOOM,
  STREET_SEGMENTS_ARROWS_LAYER,
  STREET_SEGMENTS_LAYER,
  STREET_SEGMENTS_SOURCE,
} from './constants'
import { decideRouteCameraFit, expandBounds, routeFitBoundsFingerprint, type LngLatBoundsLike } from './geojsonBuilders'
import { isDistinctFinishNode } from './finishNode'
import { pickClosestNodeId, readPropertyId } from './mapHitTest'
import { NODE_RIPPLE_DURATION_MS, nodeRippleFrame } from './nodeRipple'
import { cameraStateFromBounds, FIT_ZOOM_DIP, isSubtleCameraChange, type PlannerCameraState } from './routeCamera'

type RoutePlannerMapProps = {
  initialBounds: [[number, number], [number, number]] | null
  routeFitKey: string | null
  pathSegmentCount: number
  areaPolygon: FeatureCollection<GeoJsonPolygon>
  parkingPolygons: FeatureCollection<GeoJsonPolygon>
  streetSegments: FeatureCollection<LineString>
  pathSegments: FeatureCollection<LineString>
  continuationSegments: FeatureCollection<LineString>
  shortestPathSegments: FeatureCollection<LineString>
  gapFixSegments: FeatureCollection<LineString>
  editTailSegments: FeatureCollection<LineString>
  signPosts: FeatureCollection<Point>
  signPostAzimuths: FeatureCollection<LineString>
  displaySignPosts: boolean
  displayParkingAreas: boolean
  displayNodeNumbers: boolean
  nodes: FeatureCollection<Point>
  onNodeClick: (nodeId: string, clientPoint: { x: number; y: number }, opts?: { altKey?: boolean }) => void
  onContinuationClick: (segmentId: string) => void
  onBackgroundClick?: () => void
  onMapMoveStart?: () => void
  onViewportBoundsChange: (bounds: LngLatBoundsLike) => void
  onZoomChange: (zoom: number) => void
}

function queryInteractiveFeatures(map: MapboxGLMap, point: { x: number; y: number }, layers: string[]) {
  const pad = NODE_HIT_PIXEL_PADDING
  const bbox: [[number, number], [number, number]] = [
    [point.x - pad, point.y - pad],
    [point.x + pad, point.y + pad],
  ]
  const existingLayers = layers.filter(id => map.getLayer(id))
  if (existingLayers.length === 0) return []
  return map.queryRenderedFeatures(bbox, { layers: existingLayers })
}

/** Upper bound on per-frame fit retries (~5s at 60fps) so a map that can never
 * produce a camera (e.g. degenerate container) doesn't spin the rAF loop forever. */
const FIT_RETRY_MAX_FRAMES = 300

const EMPTY_LINE: FeatureCollection<LineString> = { type: 'FeatureCollection', features: [] }
const EMPTY_POINT: FeatureCollection<Point> = { type: 'FeatureCollection', features: [] }
const EMPTY_POLYGON: FeatureCollection<GeoJsonPolygon> = { type: 'FeatureCollection', features: [] }

export const RoutePlannerMap: FC<RoutePlannerMapProps> = props => (
  <MapProvider>
    <RoutePlannerMapInner {...props} />
  </MapProvider>
)

const RoutePlannerMapInner: FC<RoutePlannerMapProps> = ({
  initialBounds,
  routeFitKey,
  pathSegmentCount,
  areaPolygon,
  parkingPolygons,
  streetSegments,
  pathSegments,
  continuationSegments,
  shortestPathSegments,
  gapFixSegments,
  editTailSegments,
  signPosts,
  signPostAzimuths,
  displaySignPosts,
  displayParkingAreas,
  displayNodeNumbers,
  nodes,
  onNodeClick,
  onContinuationClick,
  onBackgroundClick,
  onMapMoveStart,
  onViewportBoundsChange,
  onZoomChange,
}) => {
  const { colorScheme } = useMantineColorScheme()
  const theme = useMantineTheme()
  const [cursor, setCursor] = useState<'grab' | 'pointer'>('grab')
  const mapRef = useRef<MapRef>(null)
  const fittedRouteKeyRef = useRef<string | null>(null)
  const plannerMap = useMap()[PLANNER_MAP_ID]
  useMapImage(plannerMap, FINISH_NODE_IMAGE_ID, checkeredCircle, { width: 64, height: 64 })
  useMapImage(plannerMap, PATH_DIRECTION_ARROW_IMAGE_ID, directionArrow, { width: 16, height: 16 })
  const hasStartNode = useMemo(() => nodes.features.some(feature => feature.properties?.isStartNode === true), [nodes])
  const hasEndNode = useMemo(
    () =>
      nodes.features.some(feature =>
        isDistinctFinishNode(feature.properties?.isLastNode === true, feature.properties?.isStartNode === true)
      ),
    [nodes]
  )

  useEffect(() => {
    if (!hasStartNode && !hasEndNode) return
    let rafId = 0
    const startedAt = performance.now()
    const tick = (now: number) => {
      const map = mapRef.current?.getMap()
      if (map) {
        const elapsed = now - startedAt
        const ringA = nodeRippleFrame(elapsed)
        const ringB = nodeRippleFrame(elapsed, {
          phaseOffsetMs: NODE_RIPPLE_DURATION_MS / 2,
        })
        if (hasStartNode && map.getLayer(START_NODE_RIPPLE_LAYER_A) && map.getLayer(START_NODE_RIPPLE_LAYER_B)) {
          map.setPaintProperty(START_NODE_RIPPLE_LAYER_A, 'circle-radius', ringA.radius)
          map.setPaintProperty(START_NODE_RIPPLE_LAYER_A, 'circle-stroke-opacity', ringA.opacity)
          map.setPaintProperty(START_NODE_RIPPLE_LAYER_B, 'circle-radius', ringB.radius)
          map.setPaintProperty(START_NODE_RIPPLE_LAYER_B, 'circle-stroke-opacity', ringB.opacity)
        }
        if (hasEndNode && map.getLayer(END_NODE_RIPPLE_LAYER_A) && map.getLayer(END_NODE_RIPPLE_LAYER_B)) {
          map.setPaintProperty(END_NODE_RIPPLE_LAYER_A, 'circle-radius', ringA.radius)
          map.setPaintProperty(END_NODE_RIPPLE_LAYER_A, 'circle-stroke-opacity', ringA.opacity)
          map.setPaintProperty(END_NODE_RIPPLE_LAYER_B, 'circle-radius', ringB.radius)
          map.setPaintProperty(END_NODE_RIPPLE_LAYER_B, 'circle-stroke-opacity', ringB.opacity)
        }
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [hasStartNode, hasEndNode])

  const mapStyle = useMemo(
    () => `mapbox://styles/mapbox/${colorScheme === 'light' || colorScheme === 'auto' ? 'streets-v12' : 'dark-v11'}`,
    [colorScheme]
  )

  const initialViewState = useMemo(() => {
    if (initialBounds) {
      return {
        bounds: initialBounds,
        fitBoundsOptions: {
          padding: PLANNER_FIT_BOUNDS_PADDING,
          maxZoom: PLANNER_FIT_BOUNDS_MAX_ZOOM,
        },
      }
    }
    return { zoom: PLANNER_FALLBACK_ZOOM, ...determineMapCenter() }
  }, [initialBounds])

  const publishBounds = useCallback(
    (event: MapEvent | { target: MapboxGLMap }) => {
      const map = event.target
      const b = map.getBounds()
      if (!b) return
      onViewportBoundsChange(
        expandBounds({
          west: b.getWest(),
          south: b.getSouth(),
          east: b.getEast(),
          north: b.getNorth(),
        })
      )
      onZoomChange(map.getZoom())
    },
    [onViewportBoundsChange, onZoomChange]
  )

  const fitGenerationRef = useRef(0)
  // Fitted state is only valid for the map instance it was applied to: suspense
  // hide/reveal recreates the mapbox instance while refs survive.
  const fittedMapRef = useRef<MapboxGLMap | null>(null)
  const [mapEpoch, setMapEpoch] = useState(0)
  const fitBoundsFingerprint = useMemo(
    () => routeFitBoundsFingerprint(pathSegments, pathSegmentCount, initialBounds),
    [pathSegments, pathSegmentCount, initialBounds]
  )

  const fitToLoadedRoute = useCallback((): 'done' | 'wait' | 'retry' => {
    const map = mapRef.current?.getMap()
    if (!map) return 'retry'
    const decision = decideRouteCameraFit({
      routeFitKey,
      fittedRouteKey: fittedMapRef.current === map ? fittedRouteKeyRef.current : null,
      pathSegments,
      pathSegmentCount,
      fallbackBounds: initialBounds,
    })
    if (decision.action === 'skip') return 'done'
    if (decision.action === 'wait') return 'wait'

    const camera = cameraStateFromBounds(
      map.cameraForBounds(decision.bounds, {
        padding: PLANNER_FIT_BOUNDS_PADDING,
        maxZoom: PLANNER_FIT_BOUNDS_MAX_ZOOM,
      }) ?? null,
      map.getBearing(),
      map.getPitch()
    )
    if (!camera) return 'retry'

    const from: PlannerCameraState = {
      longitude: map.getCenter().lng,
      latitude: map.getCenter().lat,
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
    }
    const targetKey = routeFitKey
    const generation = ++fitGenerationRef.current
    const subtle = isSubtleCameraChange(from, camera)

    map.stop()

    // Runs once, via whichever of moveend/timeout fires first. If the user
    // interrupts the fit animation, the user wins — no forced correction.
    let finishTimeoutId = 0
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      window.clearTimeout(finishTimeoutId)
      map.off('moveend', finish)
      if (generation !== fitGenerationRef.current) return
      if (targetKey) {
        fittedRouteKeyRef.current = targetKey
        fittedMapRef.current = map
      }
    }

    const applyFit = () => {
      if (generation !== fitGenerationRef.current) return
      if (subtle) {
        map.jumpTo({
          center: [camera.longitude, camera.latitude],
          zoom: Math.max(camera.zoom - FIT_ZOOM_DIP, 0),
          bearing: camera.bearing,
          pitch: camera.pitch,
        })
      }
      map.fitBounds(decision.bounds, {
        padding: PLANNER_FIT_BOUNDS_PADDING,
        maxZoom: PLANNER_FIT_BOUNDS_MAX_ZOOM,
        duration: PLANNER_FIT_BOUNDS_DURATION_MS,
        essential: true,
      })
      map.once('moveend', finish)
      finishTimeoutId = window.setTimeout(finish, PLANNER_FIT_BOUNDS_DURATION_MS + 120)
    }

    // stop() can leave the map mid-frame; wait two animation frames (and idle) before fitting.
    let settled = false
    const runWhenSettled = () => {
      if (settled || generation !== fitGenerationRef.current) return
      settled = true
      applyFit()
    }
    map.once('idle', runWhenSettled)
    requestAnimationFrame(() => {
      requestAnimationFrame(runWhenSettled)
    })

    return 'done'
  }, [routeFitKey, pathSegments, pathSegmentCount, initialBounds])

  const fitToLoadedRouteRef = useRef(fitToLoadedRoute)
  fitToLoadedRouteRef.current = fitToLoadedRoute

  useEffect(() => {
    if (!routeFitKey) {
      fitGenerationRef.current += 1
      fittedRouteKeyRef.current = null
      fittedMapRef.current = null
      return
    }
    if (fitBoundsFingerprint === 'pending') return

    let cancelled = false
    let rafId = 0
    let attemptsLeft = FIT_RETRY_MAX_FRAMES

    const attempt = () => {
      if (cancelled) return
      const result = fitToLoadedRouteRef.current()
      if (result === 'done' || result === 'wait') return
      attemptsLeft -= 1
      if (attemptsLeft <= 0) return
      rafId = requestAnimationFrame(attempt)
    }

    attempt()

    return () => {
      cancelled = true
      fitGenerationRef.current += 1
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [routeFitKey, fitBoundsFingerprint, mapEpoch])

  const handleLoad = useCallback(
    (event: MapEvent) => {
      publishBounds(event)
      // A freshly created map instance re-arms the fit effect (see fittedMapRef).
      setMapEpoch(epoch => epoch + 1)
    },
    [publishBounds]
  )

  const handleClick = useCallback(
    (event: MapMouseEvent) => {
      const map = event.target

      const nodeFeatures = queryInteractiveFeatures(map, event.point, [NODES_HIT_LAYER, NODES_LAYER])
      const nodeId = pickClosestNodeId(event.point, nodeFeatures, lngLat => map.project(lngLat))
      if (nodeId) {
        onNodeClick(
          nodeId,
          { x: event.originalEvent.clientX, y: event.originalEvent.clientY },
          { altKey: event.originalEvent.altKey }
        )
        return
      }

      const continuationFeatures = queryInteractiveFeatures(map, event.point, [CONTINUATION_SEGMENTS_LAYER])
      for (const feature of continuationFeatures) {
        const segmentId = readPropertyId(feature.properties?.segmentId)
        if (segmentId) {
          onContinuationClick(segmentId)
          return
        }
      }

      onBackgroundClick?.()
    },
    [onNodeClick, onContinuationClick, onBackgroundClick]
  )

  const handleMouseMove = useCallback((event: MapMouseEvent) => {
    const hits = queryInteractiveFeatures(event.target, event.point, [
      NODES_HIT_LAYER,
      NODES_LAYER,
      CONTINUATION_SEGMENTS_LAYER,
    ])
    setCursor(hits.length > 0 ? 'pointer' : 'grab')
  }, [])

  return (
    <MapboxMap
      ref={mapRef}
      id={PLANNER_MAP_ID}
      mapboxAccessToken={MAPBOX_PUBLIC_TOKEN}
      mapStyle={mapStyle}
      initialViewState={initialViewState}
      style={{ width: '100%', height: '100%' }}
      cursor={cursor}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMoveStart={onMapMoveStart}
      onMoveEnd={publishBounds}
      onLoad={handleLoad}
    >
      <AreaPolygons
        data={areaPolygon}
        options={{
          sourceId: AREA_POLYGON_SOURCE,
          fillLayerId: AREA_POLYGON_FILL_LAYER,
          lineLayerId: AREA_POLYGON_LINE_LAYER,
          fillOpacity: 0,
          fillHoverOpacity: 0,
          fillColor: '#228be6',
          lineColor: '#1971c2',
          lineWidth: 2,
        }}
      />

      <Source id={STREET_SEGMENTS_SOURCE} type="geojson" data={streetSegments}>
        <Layer
          id={STREET_SEGMENTS_LAYER}
          type="line"
          paint={{ 'line-color': PLANNER_STREET_COLOR, 'line-width': 2, 'line-opacity': 0.7 }}
        />
        <Layer
          id={STREET_SEGMENTS_ARROWS_LAYER}
          type="symbol"
          minzoom={STREET_ARROW_VISIBLE_MIN_ZOOM}
          filter={PATH_ONE_WAY_ARROWS_FILTER}
          layout={{
            'symbol-placement': 'line-center',
            'icon-image': PATH_DIRECTION_ARROW_IMAGE_ID,
            'icon-rotate': 90,
            'icon-rotation-alignment': 'map',
            'icon-size': STREET_ARROW_ICON_SIZE,
          }}
          paint={{
            'icon-opacity': STREET_ARROW_OPACITY,
          }}
        />
      </Source>

      <AreaPolygons
        data={displayParkingAreas ? parkingPolygons : EMPTY_POLYGON}
        options={{
          sourceId: PARKING_POLYGONS_SOURCE,
          fillLayerId: PARKING_POLYGONS_FILL_LAYER,
          lineLayerId: PARKING_POLYGONS_LINE_LAYER,
          fillColor: PLANNER_PARKING_FILL_COLOR,
          fillOpacity: PLANNER_PARKING_FILL_OPACITY,
          fillHoverOpacity: PLANNER_PARKING_FILL_OPACITY,
          lineColor: PLANNER_PARKING_LINE_COLOR,
          lineWidth: PLANNER_PARKING_LINE_WIDTH,
          lineHoverColor: PLANNER_PARKING_LINE_COLOR,
        }}
      />

      <Source id={CONTINUATION_SEGMENTS_SOURCE} type="geojson" data={continuationSegments}>
        <Layer
          id={CONTINUATION_SEGMENTS_LAYER}
          type="line"
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
          paint={{
            'line-color': [
              'case',
              ['boolean', ['get', 'unnecessary'], false],
              PLANNER_MUTED_COLOR,
              PLANNER_CONTINUATION_COLOR,
            ],
            'line-width': PLANNER_HINT_LINE_WIDTH,
            'line-dasharray': PLANNER_HINT_DASHARRAY,
            'line-opacity': 0.85,
          }}
        />
      </Source>

      <Source id={PATH_SEGMENTS_SOURCE} type="geojson" data={pathSegments}>
        <Layer
          id={PATH_SEGMENTS_LAYER}
          type="line"
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
          paint={{ 'line-color': PLANNER_PATH_COLOR, 'line-width': 5, 'line-opacity': 0.9 }}
        />
        <Layer
          id={PATH_SEGMENTS_ARROWS_LAYER}
          type="symbol"
          filter={PATH_ONE_WAY_ARROWS_FILTER}
          layout={{
            'symbol-placement': 'line-center',
            'icon-image': PATH_DIRECTION_ARROW_IMAGE_ID,
            'icon-rotate': 90,
            'icon-rotation-alignment': 'map',
            'icon-size': 0.65,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          }}
          paint={{
            'icon-opacity': 0.85,
          }}
        />
      </Source>

      <Source id={SHORTEST_PATH_SOURCE} type="geojson" data={shortestPathSegments}>
        <Layer
          id={SHORTEST_PATH_LAYER}
          type="line"
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
          paint={{
            'line-color': PLANNER_HINT_COLOR,
            'line-width': PLANNER_HINT_LINE_WIDTH,
            'line-dasharray': PLANNER_HINT_DASHARRAY,
            'line-opacity': 0.95,
          }}
        />
      </Source>

      <Source id={GAP_FIX_SOURCE} type="geojson" data={gapFixSegments}>
        <Layer
          id={GAP_FIX_LAYER}
          type="line"
          paint={{
            'line-color': PLANNER_GAP_FIX_COLOR,
            'line-width': 5,
            'line-dasharray': [2, 1],
            'line-opacity': 0.95,
          }}
        />
      </Source>

      <Source id={EDIT_TAIL_SOURCE} type="geojson" data={editTailSegments}>
        <Layer
          id={EDIT_TAIL_LAYER}
          type="line"
          paint={{
            'line-color': ['case', ['boolean', ['get', 'obsolete'], false], PLANNER_MUTED_COLOR, PLANNER_PATH_COLOR],
            'line-width': 5,
            'line-opacity': ['case', ['boolean', ['get', 'obsolete'], false], 0.55, 0.85],
            'line-dasharray': [2, 1],
          }}
        />
      </Source>

      <Source id={SIGN_POSTS_SOURCE} type="geojson" data={displaySignPosts ? signPosts : EMPTY_POINT}>
        <Layer
          id={SIGN_POSTS_LAYER}
          type="circle"
          minzoom={SIGN_POST_VISIBLE_MIN_ZOOM}
          paint={{
            'circle-radius': 5,
            'circle-color': 'white',
            'circle-stroke-width': 1,
            'circle-stroke-color': theme.colors.blue[9],
          }}
        />
      </Source>

      <Source id={SIGN_POST_AZIMUTHS_SOURCE} type="geojson" data={displaySignPosts ? signPostAzimuths : EMPTY_LINE}>
        <Layer
          id={SIGN_POST_AZIMUTHS_LAYER}
          type="line"
          minzoom={SIGN_POST_AZIMUTH_VISIBLE_MIN_ZOOM}
          paint={{
            'line-color': '#fcc419',
            'line-width': 2,
            'line-opacity': 0.9,
          }}
        />
      </Source>

      <Source id={NODES_SOURCE} type="geojson" data={nodes}>
        <Layer
          id={NODES_HIT_LAYER}
          type="circle"
          minzoom={NODE_VISIBLE_MIN_ZOOM}
          paint={{
            'circle-radius': 12,
            'circle-opacity': 0.01,
          }}
        />
        <Layer
          id={START_NODE_RIPPLE_LAYER_A}
          type="circle"
          minzoom={NODE_VISIBLE_MIN_ZOOM}
          filter={['boolean', ['get', 'isStartNode'], false]}
          paint={{
            'circle-radius': 8,
            'circle-color': PLANNER_START_NODE_COLOR,
            'circle-opacity': 0,
            'circle-stroke-width': 2.5,
            'circle-stroke-color': PLANNER_START_NODE_COLOR,
            'circle-stroke-opacity': 0,
          }}
        />
        <Layer
          id={START_NODE_RIPPLE_LAYER_B}
          type="circle"
          minzoom={NODE_VISIBLE_MIN_ZOOM}
          filter={['boolean', ['get', 'isStartNode'], false]}
          paint={{
            'circle-radius': 8,
            'circle-color': PLANNER_START_NODE_COLOR,
            'circle-opacity': 0,
            'circle-stroke-width': 2.5,
            'circle-stroke-color': PLANNER_START_NODE_COLOR,
            'circle-stroke-opacity': 0,
          }}
        />
        <Layer
          id={END_NODE_RIPPLE_LAYER_A}
          type="circle"
          minzoom={NODE_VISIBLE_MIN_ZOOM}
          filter={DISTINCT_FINISH_NODE_FILTER}
          paint={{
            'circle-radius': 8,
            'circle-color': PLANNER_END_NODE_RIPPLE_COLOR,
            'circle-opacity': 0,
            'circle-stroke-width': 2.5,
            'circle-stroke-color': PLANNER_END_NODE_RIPPLE_COLOR,
            'circle-stroke-opacity': 0,
          }}
        />
        <Layer
          id={END_NODE_RIPPLE_LAYER_B}
          type="circle"
          minzoom={NODE_VISIBLE_MIN_ZOOM}
          filter={DISTINCT_FINISH_NODE_FILTER}
          paint={{
            'circle-radius': 8,
            'circle-color': PLANNER_END_NODE_RIPPLE_COLOR,
            'circle-opacity': 0,
            'circle-stroke-width': 2.5,
            'circle-stroke-color': PLANNER_END_NODE_RIPPLE_COLOR,
            'circle-stroke-opacity': 0,
          }}
        />
        <Layer
          id={NODES_LAYER}
          type="circle"
          minzoom={NODE_VISIBLE_MIN_ZOOM}
          paint={{
            'circle-radius': [
              'case',
              ['boolean', ['get', 'isStartNode'], false],
              9,
              DISTINCT_FINISH_NODE_FILTER,
              4,
              ['boolean', ['get', 'isHintEnd'], false],
              8,
              ['boolean', ['get', 'isPathNode'], false],
              7,
              6,
            ],
            'circle-color': [
              'case',
              ['boolean', ['get', 'isPendingRemoval'], false],
              '#fa5252',
              ['boolean', ['get', 'isGapNode'], false],
              '#fd7e14',
              ['boolean', ['get', 'isStartNode'], false],
              PLANNER_START_NODE_COLOR,
              DISTINCT_FINISH_NODE_FILTER,
              PLANNER_END_NODE_COLOR,
              ['boolean', ['get', 'isTailEnd'], false],
              '#51cf66',
              ['boolean', ['get', 'isReconnectTarget'], false],
              '#94d82d',
              ['boolean', ['get', 'isHintEnd'], false],
              '#fab005',
              ['boolean', ['get', 'isAddTarget'], false],
              '#748ffc',
              ['boolean', ['get', 'isHintNode'], false],
              '#fcc419',
              ['boolean', ['get', 'isEditable'], false],
              '#74c0fc',
              ['boolean', ['get', 'isPathNode'], false],
              '#40c057',
              ['boolean', ['get', 'isContinuation'], false],
              PLANNER_CONTINUATION_COLOR,
              ['!', ['boolean', ['get', 'isRoutable'], true]],
              PLANNER_NON_ROUTABLE_NODE_COLOR,
              '#7950f2',
            ],
            'circle-opacity': [
              'case',
              DISTINCT_FINISH_NODE_FILTER,
              0,
              ['!', ['boolean', ['get', 'isRoutable'], true]],
              PLANNER_NON_ROUTABLE_NODE_OPACITY,
              0.95,
            ],
            'circle-stroke-width': [
              'case',
              ['boolean', ['get', 'isStartNode'], false],
              2.5,
              DISTINCT_FINISH_NODE_FILTER,
              0,
              2,
            ],
            'circle-stroke-color': [
              'case',
              ['boolean', ['get', 'isPendingRemoval'], false],
              '#c92a2a',
              ['boolean', ['get', 'isGapNode'], false],
              '#e8590c',
              ['boolean', ['get', 'isStartNode'], false],
              PLANNER_START_NODE_STROKE,
              DISTINCT_FINISH_NODE_FILTER,
              PLANNER_END_NODE_STROKE,
              ['boolean', ['get', 'isTailEnd'], false],
              '#2f9e44',
              ['boolean', ['get', 'isReconnectTarget'], false],
              '#5c940d',
              ['boolean', ['get', 'isHintEnd'], false],
              '#e67700',
              ['boolean', ['get', 'isAddTarget'], false],
              '#4c6ef5',
              ['boolean', ['get', 'isHintNode'], false],
              '#f08c00',
              ['boolean', ['get', 'isEditable'], false],
              '#1c7ed6',
              ['boolean', ['get', 'isPathNode'], false],
              '#2b8a3e',
              ['boolean', ['get', 'isContinuation'], false],
              PLANNER_CONTINUATION_STROKE,
              ['!', ['boolean', ['get', 'isRoutable'], true]],
              PLANNER_NON_ROUTABLE_NODE_STROKE,
              '#5f3dc4',
            ],
            'circle-stroke-opacity': ['case', DISTINCT_FINISH_NODE_FILTER, 0, 1],
          }}
        />
        <Layer
          id={FINISH_NODE_LAYER}
          type="symbol"
          minzoom={NODE_VISIBLE_MIN_ZOOM}
          filter={DISTINCT_FINISH_NODE_FILTER}
          layout={{
            'icon-image': FINISH_NODE_IMAGE_ID,
            'icon-size': 0.35,
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          }}
        />
        {displayNodeNumbers && (
          <Layer
            id={NODES_LABEL_LAYER}
            type="symbol"
            minzoom={NODE_VISIBLE_MIN_ZOOM}
            filter={['!=', ['get', 'label'], '']}
            layout={{
              'text-field': ['get', 'label'],
              'text-size': NODE_LABEL_TEXT_SIZE,
              'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
              'text-allow-overlap': true,
              'text-ignore-placement': true,
              'text-anchor': NODE_LABEL_TEXT_ANCHOR,
              'text-offset': NODE_LABEL_TEXT_OFFSET,
            }}
            paint={{
              'text-color': ['case', ['boolean', ['get', 'isStartNode'], false], '#ffffff', '#212529'],
              'text-opacity': 1,
              'text-halo-color': [
                'case',
                ['boolean', ['get', 'isStartNode'], false],
                PLANNER_START_NODE_STROKE,
                '#ffffff',
              ],
              'text-halo-width': NODE_LABEL_HALO_WIDTH,
              'text-halo-blur': 0,
            }}
          />
        )}
      </Source>
    </MapboxMap>
  )
}

export default RoutePlannerMap
